import type { DriverResponse } from '@contracts/driver/DriverResponse';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import type { Client } from '@src/clients/domain/Client';
import { Button } from '@src/common/components/button/Button';
import { Drawer } from '@src/common/components/Drawer';
import { IconButton } from '@src/common/components/IconButton';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { AddDriverForm } from '@src/driver/AddDriverForm';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { DriverForm } from '@src/driver/DriverForm';
import { DriverFormLoader } from '@src/driver/DriverFormLoader';
import { DriverSelector } from '@src/driver/DriverSelector';
import { EditDriverForm } from '@src/driver/EditDriverForm';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import type { DriverInformationInputsSchema } from '@src/orders/schemas/DriverInformationInputsSchema';
import {
  DriverEmailField,
  DriverIdField,
  driverInformationFields,
  DriverLicenseNumberField,
  DriverNameField,
  DriverPhoneField,
} from '@src/permitField/DriverInformationFields';
import { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type DriverInformation = yup.InferType<typeof DriverInformationInputsSchema>;

type Props = {
  client: Client;
  fields: string[];
} & CollapseListener;

const emptyDriver = {
  name: '',
  email: '',
  phone: '',
  licenseNumber: '',
};

export const DriverInformationSection = ({
  client,
  fields,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { disabled: formDisabled },
  } = useFormContext<DriverInformation>();
  const selectedDriverId = watch(DriverIdField.fieldName);
  const { isFieldDisabled } = useDisabledFields();

  const [open, setOpen] = useState(false);
  const openAddDriverModal = () => setOpen(true);
  const closeAddDriverModal = (newDriver: DriverResponse) => {
    setValue(DriverIdField.fieldName, newDriver.id);
    fillDriverSection(setValue, newDriver);
    setOpen(false);
  };

  const [editOpen, setEditOpen] = useState(false);
  const openEditDriverModal = () => setEditOpen(true);
  const closeEditDriverModal = (updatedDriver: DriverResponse) => {
    fillDriverSection(setValue, updatedDriver);
    setEditOpen(false);
  };

  const {
    current: [name, id],
  } = useRef(getValues([DriverNameField.fieldName, DriverIdField.fieldName]));

  const previousDriverValues = {
    id,
    name,
  };

  const selectDriver = (selectedDriver: DriverListing | undefined) => {
    if (selectedDriver) {
      fillDriverSection(setValue, selectedDriver);
    } else {
      fillDriverSection(setValue, emptyDriver);
    }
  };

  const { sectionInError } = useSectionError(
    Object.keys(driverInformationFields),
  );

  if (fields.length === 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('driverInformation')}
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']} alignItems="center">
          <Controller
            control={control}
            name={DriverIdField.fieldName}
            render={({ field, fieldState: { error } }) => (
              <DriverSelector
                clientId={client.id}
                error={error}
                readOnly={
                  formDisabled || isFieldDisabled(DriverIdField.fieldName)
                }
                defaultValue={
                  previousDriverValues.id && previousDriverValues.name
                    ? {
                        label: previousDriverValues.name.toString(),
                        value: previousDriverValues.id.toString(),
                      }
                    : undefined
                }
                onDriverSelect={selectDriver}
                {...field}
              />
            )}
          />
          {selectedDriverId && (
            <>
              <IconButton onClick={openEditDriverModal} title={t('editDriver')}>
                <Edit />
              </IconButton>
              <Drawer open={editOpen} onClose={() => setEditOpen(false)}>
                <PageLayout title={t('driverInformation')}>
                  <DriverFormLoader
                    driverId={selectedDriverId.toString()}
                    clientId={client.id}
                  >
                    {({ driver }) => (
                      <EditDriverForm
                        clientId={client.id}
                        driver={driver}
                        onUpdate={closeEditDriverModal}
                      />
                    )}
                  </DriverFormLoader>
                </PageLayout>
              </Drawer>
            </>
          )}
        </Stack>

        {!formDisabled && (
          <Button onClick={openAddDriverModal} endIcon={<Add />}>
            {t('addDriver')}
          </Button>
        )}

        <Drawer open={open} onClose={() => setOpen(false)}>
          <PageLayout title={t('driverInformation')}>
            <AddDriverForm
              clientId={client.id}
              onCreate={closeAddDriverModal}
            />
          </PageLayout>
        </Drawer>

        <DriverForm readOnly control={control} fields={fields} />
      </Stack>
    </EditOrderCollapsible>
  );
};

const fillDriverSection = (
  setValue: UseFormSetValue<DriverInformation>,
  previousValues: {
    name: string;
    phone?: string;
    email?: string;
    licenseNumber: string;
  },
) => {
  setValue(DriverNameField.fieldName, previousValues.name, {
    shouldValidate: true,
  });
  setValue(DriverEmailField.fieldName, previousValues.email ?? '', {
    shouldValidate: true,
  });
  setValue(DriverPhoneField.fieldName, previousValues.phone ?? '', {
    shouldValidate: true,
  });
  setValue(DriverLicenseNumberField.fieldName, previousValues.licenseNumber, {
    shouldValidate: true,
  });
};
